import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './ThankYou.css';

const ThankYou = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="thank-you-page">
      <Helmet>
        <title>Thank You | SpireNest</title>
        <link rel="canonical" href="https://spirenest.co/thank-you" />
        <meta name="description" content="Thank you for contacting SpireNest. We've received your message and will respond within 24 hours." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <section className="thank-you-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
              >
                <motion.div variants={fadeInUp} className="text-center mb-5">
                  <div className="success-icon-wrapper">
                    <motion.div
                      className="success-icon"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 200,
                        damping: 15,
                        delay: 0.2
                      }}
                    >
                      <i className="bi bi-check-circle-fill"></i>
                    </motion.div>
                  </div>

                  <h1 className="thank-you-title">Thank You!</h1>
                  <h2 className="thank-you-subtitle">We've Received Your Message</h2>
                  <p className="thank-you-description">
                    Thank you for reaching out to SpireNest. Your message has been successfully
                    received and our team will review it shortly.
                  </p>
                </motion.div>

                <motion.div variants={fadeInUp}>
                  <Card className="response-time-card border-0 shadow-sm mb-4">
                    <Card.Body className="text-center py-4">
                      <div className="response-time-icon mb-3">
                        <i className="bi bi-clock-history"></i>
                      </div>
                      <h5 className="mb-2">Expected Response Time</h5>
                      <p className="response-time-text mb-0">Within 24 hours</p>
                    </Card.Body>
                  </Card>
                </motion.div>

                <motion.div variants={fadeInUp}>
                  <Card className="what-next-card border-0 shadow-sm mb-4">
                    <Card.Body className="p-4">
                      <h4 className="what-next-title mb-4">What happens next?</h4>

                      <div className="steps-container">
                        <div className="step-item">
                          <div className="step-number">1</div>
                          <div className="step-content">
                            <h6>Our team reviews your inquiry</h6>
                            <p>We carefully read your message to understand your needs</p>
                          </div>
                        </div>

                        <div className="step-item">
                          <div className="step-number">2</div>
                          <div className="step-content">
                            <h6>We assign the right specialist to help you</h6>
                            <p>A NetSuite expert best suited for your requirements will be assigned</p>
                          </div>
                        </div>

                        <div className="step-item">
                          <div className="step-number">3</div>
                          <div className="step-content">
                            <h6>You'll receive a personalized response</h6>
                            <p>We'll reach out with tailored solutions and next steps</p>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>

                <motion.div variants={fadeInUp}>
                  <Card className="immediate-assistance-card border-0 shadow-sm mb-4">
                    <Card.Body className="p-4">
                      <h5 className="assistance-title mb-3">Need immediate assistance?</h5>
                      <Row>
                        <Col md={6} className="mb-3 mb-md-0">
                          <div className="contact-method">
                            <i className="bi bi-telephone-fill me-2"></i>
                            <div>
                              <small className="d-block text-muted">Call us at</small>
                              <a href="tel:+16509632560" className="contact-link">
                                +1 (650) 9632560
                              </a>
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="contact-method">
                            <i className="bi bi-envelope-fill me-2"></i>
                            <div>
                              <small className="d-block text-muted">Email us at</small>
                              <a href="mailto:business@spirenest.co" className="contact-link">
                                business@spirenest.co
                              </a>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </motion.div>

                <motion.div variants={fadeInUp} className="text-center mt-5">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => navigate('/')}
                    className="me-3 px-5"
                  >
                    <i className="bi bi-house-fill me-2"></i>
                    Back to Home
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="lg"
                    onClick={() => navigate('/netsuite/services')}
                    className="px-5"
                  >
                    Explore Our Services
                  </Button>
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default ThankYou;
